import React, { useState, useEffect } from 'react';
import { hasPermission } from '../utils/permissions';
import { Switch, FormControlLabel } from '@mui/material';
import { useData } from '../hooks/useData';
import { apiFetch } from '../api';
// @ts-ignore
import { StandardPageLayout, Button as CommonButton, LoadingSpinner, Modal, ConfirmModal, FormInput, FormSelect, FormTextarea, SortDropdown } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';
import { notify } from '@/components/core';
import { useAlerts } from '../context/AlertContext';
import {
    PlusCircle, Search, LayoutGrid, List, MoreHorizontal,
    BellRing, Trash2, Edit, Loader2, Mail, Smartphone, Bell, ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const AlertRulesPage = ({ token, currentUser }) => {

    const { refreshAlerts } = useAlerts();

    // Admin State
    const isAdmin = hasPermission(currentUser, 'tenants:view_all'); // Cross-tenant alert visibility
    const [showAllTenants, setShowAllTenants] = useState(false);
    const [filterTenantId, setFilterTenantId] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [viewLayout, setViewLayout] = useState('grid');
    const [sortOption, setSortOption] = useState('updated_desc');

    // Fetch Rules
    const endpoint = (isAdmin && showAllTenants) ? '/alerts/rules?all_tenants=true' : '/alerts/rules';
    const { data: rulesResponse, isLoading: loadingRules, refresh } = useData(endpoint, token);
    const rules = rulesResponse?.rules || [];

    // Fetch Tenants for Filtering (only if Admin)
    const { data: tenantsList } = useData(isAdmin ? '/tenants' : null, token);
    const tenantsMap = (tenantsList || []).reduce((acc, t) => ({ ...acc, [t.id]: t.name }), {});

    // Modal & Selection State
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState(null);

    // Form State
    const initialFormState = {
        name: '',
        notify_in_app: true,
        notify_email: false,
        email_string: '', // Helper for UI
        email_recipients: [],
        rule_condition: {
            on_status: ['failed'],
            job_types: []
        }
    };
    const [formData, setFormData] = useState(initialFormState);

    // Constant Options
    const STATUS_OPTIONS = ['completed', 'failed', 'running', 'pending'];
    const JOB_TYPE_OPTIONS = ['ML Model', 'GenAI Usecase', 'Workflow App', 'Data Model', 'Quota', 'System'];

    // --- Effects & Handlers ---

    useEffect(() => {
        if (editingRule) {
            setFormData({
                name: editingRule.name || '',
                notify_in_app: editingRule.notify_in_app,
                notify_email: editingRule.notify_email,
                email_recipients: editingRule.email_recipients || [],
                email_string: (editingRule.email_recipients || []).join(', '),
                rule_condition: {
                    on_status: editingRule.rule_condition?.on_status || [],
                    job_types: editingRule.rule_condition?.job_types || []
                }
            });
        } else {
            setFormData(initialFormState);
        }
    }, [editingRule]);

    const filteredRules = rules.filter(rule => {
        const matchesSearch = rule.name.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesTenant = filterTenantId === 'all' || rule.tenant_id === filterTenantId;

        // Specialized Partner Logic: 
        // If Partner Admin is viewing, they see their sub-tenants automatically. 
        // The `filterTenantId` is purely client-side filtering unless the list is huge.

        return matchesSearch && matchesTenant;
    });
    const sortedRules = [...filteredRules].sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
        const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
        if (sortOption === 'updated_desc') return dateB - dateA;
        if (sortOption === 'updated_asc') return dateA - dateB;
        if (sortOption === 'name_asc') return (a.name || '').localeCompare(b.name || '');
        if (sortOption === 'name_desc') return (b.name || '').localeCompare(a.name || '');
        return 0;
    });

    const openCreateModal = () => {
        setEditingRule(null);
        setIsModalOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();

        // Parse emails
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const recipients = formData.email_string
            .split(',')
            .map(e => e.trim())
            .filter(e => emailRegex.test(e));

        const payload = {
            name: formData.name,
            notify_in_app: formData.notify_in_app,
            notify_email: formData.notify_email,
            email_recipients: recipients,
            rule_condition: formData.rule_condition
        };

        const action = editingRule ? 'Updated' : 'Created';
        try {
            if (editingRule) {
                await apiFetch(`/alerts/rules/${editingRule.id}`, token, {
                    method: 'PUT',
                    body: JSON.stringify(payload)
                });
            } else {
                await apiFetch('/alerts/rules', token, {
                    method: 'POST',
                    body: JSON.stringify(payload)
                });
            }
            notify.success(`Alert Rule ${action} successfully.`);
            setIsModalOpen(false);
            refresh();
        } catch (err) {
            notify.error(err.message);
        }
    };

    const handleDelete = async () => {
        if (!confirmDelete) return;
        try {
            await apiFetch(`/alerts/rules/${confirmDelete.id}`, token, { method: 'DELETE' });
            notify.success('Alert Rule deleted.');
            setConfirmDelete(null);
            refresh();
        } catch (err) {
            notify.error(err.message);
        }
    };

    const handleToggleActive = async (rule) => {
        try {
            await apiFetch(`/alerts/rules/${rule.id}`, token, {
                method: 'PUT',
                body: JSON.stringify({ is_active: !rule.is_active })
            });
            refresh();
        } catch (err) {
            notify.error(err.message);
        }
    };

    const handleTest = async (rule) => {
        try {
            await apiFetch(`/alerts/rules/${rule.id}/test`, token, { method: 'POST' });
            notify.success('Test alert sent.');
            refreshAlerts(); // Force immediate update of notification badge
        } catch (err) {
            notify.error(err.message);
        }
    };

    // Helper to toggle array items
    const toggleCondition = (key, value) => {
        setFormData(prev => {
            const list = prev.rule_condition[key] || [];
            const exists = list.includes(value);
            const newList = exists ? list.filter(i => i !== value) : [...list, value];
            return {
                ...prev,
                rule_condition: {
                    ...prev.rule_condition,
                    [key]: newList
                }
            };
        });
    };

    // Header & Filter Components
    const headerActions = (
        <CommonButton onClick={openCreateModal} sx={{ height: 30 }}>
            <PlusCircle className="mr-2 h-4 w-4" /> NEW RULE
        </CommonButton>
    );

    const filterControls = (
        <div className="flex items-center gap-4">
            <div className="relative max-w-md group w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 group-focus-within:text-[#002F5D] dark:text-slate-500 transition-colors" />
                <Input
                    type="text"
                    placeholder="Search rules..."
                    className="pl-10 bg-white dark:bg-slate-900"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                />
            </div>

            {isAdmin && (
                <div className="flex items-center gap-4">
                    <Select
                        value={filterTenantId}
                        onValueChange={setFilterTenantId}
                        disabled={!showAllTenants}
                    >
                        <SelectTrigger className="w-[180px] h-9 text-sm bg-white dark:bg-slate-900">
                            <SelectValue placeholder="All Tenants" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All Tenants</SelectItem>
                            {tenantsList?.map(t => (
                                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={showAllTenants}
                                onChange={(e) => setShowAllTenants(e.target.checked)}
                                size="small"
                            />
                        }
                        label={<span className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">All Tenants</span>}
                        className="mr-0"
                    />
                </div>
            )}

            <div className="flex items-center bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-800 p-1 ml-auto">
                <Button variant={viewLayout === 'grid' ? 'secondary' : 'ghost'} size="sm" onClick={() => setViewLayout('grid')} className={`px-2 h-8 ${viewLayout === 'grid' ? 'dark:bg-slate-800 dark:text-white' : 'dark:text-slate-400'}`}>
                    <LayoutGrid className="w-4 h-4" />
                </Button>
                <Button variant={viewLayout === 'list' ? 'secondary' : 'ghost'} size="sm" onClick={() => setViewLayout('list')} className={`px-2 h-8 ${viewLayout === 'list' ? 'dark:bg-slate-800 dark:text-white' : 'dark:text-slate-400'}`}>
                    <List className="w-4 h-4" />
                </Button>
            </div>
            <SortDropdown value={sortOption} onChange={setSortOption} />
        </div>
    );

    const renderCard = (rule) => (
        <div key={rule.id} className="group bg-white dark:bg-slate-900 rounded-2xl p-5 border border-gray-100 dark:border-slate-800 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer relative overflow-hidden flex flex-col h-full">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity" />

            <div className="flex justify-between items-start mb-4">
                <div className={`flex items-center justify-center p-2.5 rounded-xl group-hover:text-white transition-colors duration-300 ${rule.is_active ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 group-hover:bg-green-600' : 'bg-gray-50 dark:bg-gray-800 text-gray-400 group-hover:bg-gray-500'}`}>
                    <Bell className="h-6 w-6" />
                </div>
                <div className="flex gap-1">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300">
                                <MoreHorizontal className="w-4 h-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setEditingRule(rule); setIsModalOpen(true); }}>
                                <Edit className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleTest(rule); }}>
                                <BellRing className="w-4 h-4 mr-2" /> Send Test
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setConfirmDelete(rule); }} className="text-red-600">
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            </div>

            <div className="mb-4 flex-grow">
                <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold text-gray-900 dark:text-slate-100 group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors truncate">{rule.name}</h3>
                    {isAdmin && showAllTenants && rule.tenant_id && (
                        <span className="text-[10px] bg-gray-100 dark:bg-gray-800 text-gray-500 px-1.5 py-0.5 rounded">
                            {tenantsMap[rule.tenant_id] || 'Tenant'}
                        </span>
                    )}
                </div>

                <div className="flex flex-wrap gap-1 mt-2 mb-3">
                    {rule.rule_condition?.on_status?.map(s => (
                        <span key={s} className="px-2 py-0.5 rounded text-[10px] uppercase font-bold bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-300 border border-red-100 dark:border-red-800">
                            {s}
                        </span>
                    ))}
                    {rule.rule_condition?.job_types?.length > 0 && rule.rule_condition.job_types.map(t => (
                        <span key={t} className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-100 dark:border-blue-800">
                            {t}
                        </span>
                    ))}
                </div>

                <div className="flex gap-2 mt-2">
                    {rule.notify_in_app && (
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                            <Smartphone className="w-3 h-3" /> App
                        </div>
                    )}
                    {rule.notify_email && (
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                            <Mail className="w-3 h-3" /> Email
                        </div>
                    )}
                </div>
            </div>

            <div className="pt-4 border-t border-gray-50 dark:border-slate-800 flex justify-between items-center">
                <div className="flex items-center gap-2">
                    <Switch checked={rule.is_active} onChange={(e) => { e.stopPropagation(); handleToggleActive(rule); }} size="small" />
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{rule.is_active ? 'Active' : 'Inactive'}</span>
                </div>
            </div>
        </div>
    );

    const renderListView = (items) => (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm overflow-hidden">
            <Table>
                <TableHeader className="bg-gray-50/50 dark:bg-slate-900/50">
                    <TableRow className="border-b border-gray-200 dark:border-slate-800">
                        <TableHead>Name</TableHead>
                        {isAdmin && showAllTenants && <TableHead>Tenant</TableHead>}
                        <TableHead>Triggers</TableHead>
                        <TableHead>Channels</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {items.map(rule => (
                        <TableRow key={rule.id} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/50 transition-colors border-b border-gray-100 dark:border-slate-800">
                            <TableCell className="font-medium text-gray-900 dark:text-slate-100">{rule.name}</TableCell>
                            {isAdmin && showAllTenants && (
                                <TableCell className="text-gray-500 dark:text-slate-400">
                                    {rule.tenant_id ? (tenantsMap[rule.tenant_id] || rule.tenant_id) : <span className="text-blue-500">System</span>}
                                </TableCell>
                            )}
                            <TableCell>
                                <div className="flex flex-wrap gap-1">
                                    {rule.rule_condition?.on_status?.map(s => (
                                        <span key={s} className="px-2 py-0.5 rounded text-xs bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300 border border-red-100 dark:border-red-800">
                                            {s}
                                        </span>
                                    ))}
                                    {rule.rule_condition?.job_types?.length > 0 && rule.rule_condition.job_types.map(t => (
                                        <span key={t} className="px-2 py-0.5 rounded text-xs bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-100 dark:border-blue-800">
                                            {t}
                                        </span>
                                    ))}
                                </div>
                            </TableCell>
                            <TableCell>
                                <div className="flex gap-2">
                                    {rule.notify_in_app && <DynamicIcon name="Smartphone" className="w-4 h-4 text-purple-500" title="In-App" />}
                                    {rule.notify_email && <DynamicIcon name="Mail" className="w-4 h-4 text-green-500" title="Email" />}
                                </div>
                            </TableCell>
                            <TableCell>
                                <div className="flex items-center gap-2">
                                    <Switch checked={rule.is_active} onChange={() => handleToggleActive(rule)} size="small" />
                                </div>
                            </TableCell>
                            <TableCell className="text-right">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:hover:text-slate-300">
                                            <MoreHorizontal className="w-4 h-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem onClick={() => { setEditingRule(rule); setIsModalOpen(true); }}>
                                            <Edit className="w-4 h-4 mr-2" /> Edit
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => handleTest(rule)}>
                                            <BellRing className="w-4 h-4 mr-2" /> Send Test
                                        </DropdownMenuItem>
                                        <DropdownMenuItem onClick={() => setConfirmDelete(rule)} className="text-red-600">
                                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );

    return (
        <StandardPageLayout
            title="Alert Rules"
            description="Set up rules to notify you about important events."
            headerActions={headerActions}
            filterControls={filterControls}
        >
            <div className="pb-10 min-h-[50vh]">
                {loadingRules ? <LoadingSpinner /> : (
                    <>
                        {sortedRules.length === 0 ? (
                            <div className="text-center py-12">
                                <div className="bg-slate-50 dark:bg-slate-900/50 p-6 rounded-full w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                                    <Bell className="h-10 w-10 text-slate-300 dark:text-slate-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                                    No alerts found
                                </h3>
                                <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-sm mx-auto">
                                    {searchQuery
                                        ? 'Try adjusting your search query'
                                        : 'Create a new alert rule to get notified about important events.'}
                                </p>
                                <Button variant="ghost" onClick={openCreateModal} className="gap-2">
                                    Create Rule <ArrowRight className="h-4 w-4" />
                                </Button>
                            </div>
                        ) : (
                            viewLayout === 'list' ? (
                                renderListView(sortedRules)
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                    {sortedRules.map(rule => renderCard(rule))}
                                </div>
                            )
                        )}
                    </>
                )}
            </div>

            {/* Create/Edit Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={editingRule ? 'Edit Alert Rule' : 'Create Alert Rule'}
            >
                <form onSubmit={handleSave} className="space-y-4">
                    <FormInput
                        name="name"
                        label="Rule Name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                    />

                    {/* Condition: Status */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Trigger on Status</label>
                        <div className="flex flex-wrap gap-2">
                            {STATUS_OPTIONS.map(status => (
                                <button
                                    key={status}
                                    type="button"
                                    onClick={() => toggleCondition('on_status', status)}
                                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${formData.rule_condition.on_status.includes(status)
                                        ? 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900 dark:text-red-200 dark:border-red-800'
                                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600'
                                        }`}
                                >
                                    {status}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Condition: Job Type */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Filter by Job Type (Optional)</label>
                        <div className="flex flex-wrap gap-2">
                            {JOB_TYPE_OPTIONS.map(type => (
                                <button
                                    key={type}
                                    type="button"
                                    onClick={() => toggleCondition('job_types', type)}
                                    className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${formData.rule_condition.job_types.includes(type)
                                        ? 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-800'
                                        : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600'
                                        }`}
                                >
                                    {type}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Channels */}
                    <div className="space-y-3 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <Switch checked={formData.notify_in_app} onChange={(e) => setFormData({ ...formData, notify_in_app: e.target.checked })} />
                            <span className="text-sm">In-App Notifications</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <Switch checked={formData.notify_email} onChange={(e) => setFormData({ ...formData, notify_email: e.target.checked })} />
                            <span className="text-sm">Email Notifications</span>
                        </label>

                        {formData.notify_email && (
                            <FormTextarea
                                name="email_string"
                                label="Recipients (comma separated)"
                                value={formData.email_string}
                                onChange={(e) => setFormData({ ...formData, email_string: e.target.value })}
                                placeholder="admin@example.com, dev@example.com"
                                rows={2}
                            />
                        )}
                    </div>

                    <div className="flex justify-end gap-3 pt-4">
                        <Button variant="secondary" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                        <Button type="submit">Save Rule</Button>
                    </div>
                </form>
            </Modal>

            <ConfirmModal
                isOpen={!!confirmDelete}
                onClose={() => setConfirmDelete(null)}
                onConfirm={handleDelete}
                title="Delete Rule?"
            >
                Are you sure? This will stop alerts from being sent for this rule.
            </ConfirmModal>

        </StandardPageLayout>
    );
};

export default AlertRulesPage;
